import React, { useState, useCallback, useMemo, useEffect } from "react";
import axios from "axios";
import ReactApexChart from "react-apexcharts";
import { useTitle } from "../../../../hooks/useTitle";
import { withAuth } from "../../../../components/HOC/withAuth";
import CardContainer from "../../../../components/CustomUI/CardContainer";
import { SectionSpinner, SectionEmpty } from "../shared/utils";
import { API_URL } from "../shared/constants";
import {
  dayBoundsInAppTz,
  formatDateTimeTrInAppTz,
  formatInstantDateShortTr,
} from "../../../../utils/appTime";
import OEEReportShell from "./OEEReportShell";
import { useOEEReport } from "./useOEEReport";
import "../Reports.scss";

const WEEKLY_METRICS = [
  { key: "teep", label: "TEEP", color: "#8b5cf6" },
  { key: "oee", label: "OEE", color: "#22c55e" },
  { key: "availability", label: "Kullanılabilirlik", color: "#1e3a8a" },
  { key: "performance", label: "Performans", color: "#f97316" },
];

const fmtDMY = (d) => formatInstantDateShortTr(d);
const fmtDMYHM = (d) => formatDateTimeTrInAppTz(d);

const buildWeeklyBuckets = (startStr, endStr) => {
  const start = dayBoundsInAppTz(String(startStr).slice(0, 10)).start;
  const end = dayBoundsInAppTz(String(endStr).slice(0, 10)).end;
  const buckets = [];
  const cursor = new Date(start);
  const dow = (cursor.getDay() + 6) % 7;
  cursor.setDate(cursor.getDate() - dow);
  cursor.setHours(0, 0, 0, 0);
  while (cursor <= end) {
    const wEnd = new Date(cursor);
    wEnd.setDate(wEnd.getDate() + 7);
    const bs = cursor < start ? new Date(start) : new Date(cursor);
    const be = wEnd > end ? new Date(end) : new Date(wEnd);
    buckets.push({
      label: fmtDMY(new Date(cursor)),
      startISO: bs.toISOString(),
      endISO: be.toISOString(),
    });
    cursor.setDate(cursor.getDate() + 7);
  }
  return buckets;
};

const OEESummaryChart = ({ metrics }) => {
  if (!metrics) return null;

  const items = [
    { label: "OEE", value: metrics.oee ?? 0, color: "#22c55e" },
    { label: "TEEP", value: metrics.teep ?? 0, color: "#8b5cf6" },
    {
      label: "Kullanılabilirlik",
      value: metrics.availability ?? 0,
      color: "#1e3a8a",
    },
    { label: "Performans", value: metrics.performance ?? 0, color: "#f97316" },
    { label: "Kalite", value: metrics.quality ?? 0, color: "#ef4444" },
  ];

  return (
    <CardContainer title="Özet Rapor">
      <ReactApexChart
        type="bar"
        series={[{ name: "%", data: items.map((i) => i.value) }]}
        options={{
          chart: {
            type: "bar",
            toolbar: { show: false },
            fontFamily: "inherit",
            foreColor: "#334155",
          },
          colors: items.map((i) => i.color),
          plotOptions: {
            bar: {
              horizontal: false,
              borderRadius: 6,
              distributed: true,
              columnWidth: "55%",
              dataLabels: { position: "top" },
            },
          },
          xaxis: { categories: items.map((i) => i.label) },
          yaxis: {
            min: 0,
            max: Math.max(100, Math.ceil(Math.max(...items.map((i) => i.value || 0)) / 10) * 10),
            labels: { formatter: (v) => `${Math.round(v)}%` },
          },
          dataLabels: {
            enabled: true,
            formatter: (v) => `${Math.round(v)}%`,
            offsetY: -18,
            style: { fontSize: "11px", fontWeight: 700 },
          },
          legend: { show: false },
          grid: { borderColor: "#f0f2f5" },
          tooltip: { y: { formatter: (v) => `${v}%` } },
          annotations: {
            yaxis: [
              { y: 80, borderColor: "#22c55e", strokeDashArray: 4 },
              { y: 60, borderColor: "#f97316", strokeDashArray: 4 },
            ],
          },
        }}
        height={320}
      />
    </CardContainer>
  );
};

const WeeklyTrendChart = ({ buckets, series, filters, machines, loading }) => {
  const [visible, setVisible] = useState(() =>
    WEEKLY_METRICS.reduce((acc, m) => ({ ...acc, [m.key]: true }), {}),
  );
  const allOn = WEEKLY_METRICS.every((m) => visible[m.key]);

  const toggleAll = () => {
    const next = !allOn;
    setVisible(
      WEEKLY_METRICS.reduce((acc, m) => ({ ...acc, [m.key]: next }), {}),
    );
  };

  const machineLabel =
    filters.machineIds.length > 0
      ? machines
          .filter((m) =>
            filters.machineIds.some((id) => String(id) === String(m._id)),
          )
          .map((m) => m.name)
          .join(", ") || "Hepsi"
      : "Hepsi";

  const periodStart = new Date(`${filters.startDate}T00:00:00`);
  const periodEnd = new Date(`${filters.endDate}T23:59:59`);
  const title = `Verimlilik Trendi  [${fmtDMYHM(periodStart)} .. ${fmtDMYHM(periodEnd)}]  [${machineLabel}]`;

  const apexSeries = useMemo(
    () =>
      WEEKLY_METRICS.filter((m) => visible[m.key]).map((m) => ({
        name: m.label,
        data: series.map((row) => row?.metrics?.[m.key] ?? null),
        color: m.color,
      })),
    [series, visible],
  );

  const categories = buckets.map((b) => b.label);

  const options = {
    chart: {
      type: "line",
      fontFamily: "inherit",
      foreColor: "#334155",
      toolbar: {
        show: true,
        tools: {
          download: true,
          selection: false,
          zoom: false,
          zoomin: false,
          zoomout: false,
          pan: false,
          reset: false,
        },
      },
      animations: { enabled: true, speed: 400 },
    },
    title: {
      text: title,
      align: "center",
      style: { fontSize: "11px", fontWeight: 600, color: "#334155" },
    },
    colors: WEEKLY_METRICS.filter((m) => visible[m.key]).map((m) => m.color),
    stroke: { width: 2.5, curve: "smooth" },
    markers: { size: 4, strokeWidth: 0, hover: { size: 6 } },
    dataLabels: { enabled: false },
    xaxis: {
      categories,
      labels: { rotate: -45, style: { fontSize: "11px", colors: "#475569" } },
      axisBorder: { color: "#e2e8f0" },
    },
    yaxis: {
      min: 0,
      max: 110,
      tickAmount: 11,
      labels: {
        formatter: (v) => Math.round(v),
        style: { fontSize: "11px", colors: "#64748b" },
      },
    },
    legend: { show: true, position: "top", horizontalAlign: "right" },
    grid: { borderColor: "#e8eaed", strokeDashArray: 3 },
    annotations: {
      yaxis: [
        {
          y: 80,
          borderColor: "#22c55e",
          strokeDashArray: 4,
          label: {
            text: "80% (İyi)",
            style: { background: "#22c55e", color: "#fff", fontSize: "10px" },
          },
        },
        {
          y: 60,
          borderColor: "#f97316",
          strokeDashArray: 4,
          label: {
            text: "60% (Orta)",
            style: { background: "#f97316", color: "#fff", fontSize: "10px" },
          },
        },
      ],
    },
    tooltip: {
      shared: true,
      intersect: false,
      y: { formatter: (v) => (v == null ? "—" : `${v}%`) },
    },
  };

  return (
    <CardContainer title="Verimlilik — Haftalık Değişim">
      <div className="d-flex" style={{ gap: 16, alignItems: "flex-start" }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <SectionSpinner />
          ) : buckets.length === 0 || series.length === 0 ? (
            <SectionEmpty msg="Haftalık veri yok." />
          ) : (
            <ReactApexChart
              type="line"
              height={360}
              series={apexSeries}
              options={options}
            />
          )}
        </div>
        <div style={{ width: 190, paddingTop: 32 }}>
          <label
            className="d-flex align-items-center"
            style={{
              gap: 8,
              cursor: "pointer",
              marginBottom: 10,
              padding: "6px 8px",
              border: "1px solid #e2e8f0",
              borderRadius: 6,
            }}
          >
            <input type="checkbox" checked={allOn} onChange={toggleAll} />
            <span style={{ fontWeight: 600, fontSize: 13 }}>Hepsini Seç</span>
          </label>
          {WEEKLY_METRICS.map((m) => (
            <label
              key={m.key}
              className="d-flex align-items-center"
              style={{
                gap: 8,
                cursor: "pointer",
                padding: "4px 8px",
                fontSize: 13,
              }}
            >
              <input
                type="checkbox"
                checked={!!visible[m.key]}
                onChange={() =>
                  setVisible((p) => ({ ...p, [m.key]: !p[m.key] }))
                }
              />
              <span
                style={{
                  display: "inline-block",
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  backgroundColor: m.color,
                }}
              />
              <span>{m.label}</span>
            </label>
          ))}
        </div>
      </div>
    </CardContainer>
  );
};

const OEEReport = () => {
  useTitle("Rovion MES - OEE");
  const {
    filters,
    machines,
    shifts,
    teepData,
    loading,
    fetchData,
    toasts,
    dismissToast,
    authH,
  } = useOEEReport();

  const [weeklyBuckets, setWeeklyBuckets] = useState([]);
  const [weeklySeries, setWeeklySeries] = useState([]);
  const [weeklyLoading, setWeeklyLoading] = useState(false);

  const fetchWeekly = useCallback(async () => {
    const buckets = buildWeeklyBuckets(filters.startDate, filters.endDate);
    setWeeklyBuckets(buckets);
    if (buckets.length === 0) {
      setWeeklySeries([]);
      return;
    }
    setWeeklyLoading(true);
    try {
      const results = await Promise.all(
        buckets.map((b) => {
          const p = new URLSearchParams();
          p.set("startDate", b.startISO);
          p.set("endDate", b.endISO);
          if (filters.machineIds.length)
            p.set("machineIds", filters.machineIds.join(","));
          if (filters.shiftId) p.set("shiftId", filters.shiftId);
          return axios
            .get(`${API_URL}/api/teep?${p}`, authH())
            .then((r) => r.data.data)
            .catch(() => null);
        }),
      );
      setWeeklySeries(results);
    } finally {
      setWeeklyLoading(false);
    }
  }, [
    filters.startDate,
    filters.endDate,
    filters.machineIds,
    filters.shiftId,
    authH,
  ]);

  const handleApply = useCallback(async () => {
    await fetchData();
    fetchWeekly();
  }, [fetchData, fetchWeekly]);

  useEffect(() => {
    fetchWeekly();
  }, []); // eslint-disable-line

  const renderContent = () => {
    if (loading) return <SectionSpinner />;
    if (!teepData)
      return <SectionEmpty msg="Filtreyi uygulayarak hesaplamayı başlatın." />;

    const { metrics } = teepData;

    return (
      <div className="d-flex flex-column gap-3">
        <OEESummaryChart metrics={metrics} />
        <WeeklyTrendChart
          buckets={weeklyBuckets}
          series={weeklySeries}
          filters={filters}
          machines={machines}
          loading={weeklyLoading}
        />
      </div>
    );
  };

  return (
    <OEEReportShell
      title="Özet Rapor"
      filters={filters}
      machines={machines}
      shifts={shifts}
      onApply={handleApply}
      toasts={toasts}
      dismissToast={dismissToast}
    >
      {renderContent()}
    </OEEReportShell>
  );
};

export default withAuth(OEEReport);
